import React from 'react';
import { ApolloError, useQuery } from '@apollo/client';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  alpha,
  Box,
  Button,
  Chip,
  FormControl,
  FormControlLabel,
  FormLabel,
  LinearProgress,
  MenuItem,
  Radio,
  RadioGroup,
  Skeleton,
  TextField,
  Typography,
} from '@mui/material';
import PhoneForwardedOutlinedIcon from '@mui/icons-material/PhoneForwardedOutlined';

import { Item } from '../../../styles/common';
import { CRM_PATHS } from '../crmPaths';
import { CRM_CONTACT_FILTER_OPTIONS } from '../contacts/contacts.queries';
import { ContactListState, describeListError, EMPTY_STATE, hasFilters, toSearchParams, withFilterChange } from '../contacts/contactList';
import { FilterBar } from '../contacts/filters/FilterBar';
import { FacetOption, FilterOptions, ListOption, TagOption } from '../contacts/filters/types';
import { outcomeLabel } from '../calls/logCall';
import {
  describeTally,
  people,
  QUEUE_MODES,
  QueueMode,
  queueRows,
  QueueSettings,
  QueueSummary,
  REST_CHOICES,
  Tally,
  waitingRows,
} from './callQueue';

const n = (value: number) => value.toLocaleString('en-US');

const Section = ({ title, id, children }: { title: string; id: string; children: React.ReactNode }) => (
  <Box component="section" aria-labelledby={id}>
    <Typography id={id} variant="subtitle1" component="h2" sx={{ fontWeight: 600, mb: 1 }}>
      {title}
    </Typography>
    {children}
  </Box>
);

/** One row of the summary: what, and how many, with a bar for its share. */
const Row = ({ label, count, of, tone }: { label: string; count: number; of: number; tone: string }) => (
  <Box component="li" sx={{ display: 'grid', gridTemplateColumns: '1fr auto', columnGap: 1.5, alignItems: 'baseline', py: 0.5 }}>
    <Typography variant="body2">{label}</Typography>
    <Typography variant="body2" sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
      {n(count)}
    </Typography>
    <Box aria-hidden sx={{ gridColumn: '1 / -1', height: 4, borderRadius: 2, bgcolor: '#eef1f5', mt: 0.5, overflow: 'hidden' }}>
      <Box sx={{ height: '100%', width: `${of ? Math.max(count ? 1.5 : 0, (count / of) * 100) : 0}%`, bgcolor: tone, borderRadius: 2 }} />
    </Box>
  </Box>
);

interface QueueSetupProps {
  settings: QueueSettings;
  onChange: (settings: QueueSettings) => void;
  summary: QueueSummary | undefined;
  summaryLoading: boolean;
  summaryError: ApolloError | undefined;
  onStart: () => void;
  starting: boolean;
  /** The session just ended, if any: what the caller did. */
  lastSession: { tally: Tally; endedAt: number } | null;
  /** Something went wrong serving the first person. */
  startError: string | null;
}

/**
 * Before calling (card 21): who to call, with the contact list's own filters;
 * in what order; and how many that makes, with why the rest of the filter is
 * not in the queue. "Start calling" serves the first person.
 */
export const QueueSetup = ({
  settings,
  onChange,
  summary,
  summaryLoading,
  summaryError,
  onStart,
  starting,
  lastSession,
  startError,
}: QueueSetupProps) => {
  const optionsQuery = useQuery<{
    crmTags: Array<TagOption>;
    countries: Array<FacetOption>;
    lists: { total: number; items: Array<ListOption> };
  }>(CRM_CONTACT_FILTER_OPTIONS);
  const options: FilterOptions | undefined = optionsQuery.data && {
    tags: optionsQuery.data.crmTags,
    countries: optionsQuery.data.countries,
    lists: optionsQuery.data.lists.items,
    listTotal: optionsQuery.data.lists.total,
  };

  const list = settings.list;
  const changeList = (partial: Partial<ContactListState>) => onChange({ ...settings, list: withFilterChange(list, partial) });
  const matched = summary
    ? summary.inQueue + summary.resting + summary.followUpsLater + summary.beingCalled + summary.cannotCall +
      (settings.mode === 'FIRST' ? 0 : summary.neverCalled + summary.notCalledRecently)
    : null;
  const contactsLink = `${CRM_PATHS.contacts}${hasFilters(list) ? `?${toSearchParams({ ...list, sort: null, page: 0 })}` : ''}`;
  const rows = summary ? queueRows(summary, settings.mode) : [];
  const waiting = summary ? waitingRows(summary, settings.mode) : [];

  return (
    <Box sx={{ display: 'grid', gap: 2 }}>
      {lastSession && (
        <Alert severity="success" variant="outlined">
          Session ended: {describeTally(lastSession.tally, lastSession.endedAt)}{' '}
          {Object.entries(lastSession.tally.outcomes)
            .map(([outcome, count]) => `${outcomeLabel(outcome)} ${count}`)
            .join(' · ')}
        </Alert>
      )}

      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) 360px' }, alignItems: 'start' }}>
        <Box sx={{ display: 'grid', gap: 2, minWidth: 0 }}>
          <Item sx={{ p: { xs: 1.5, sm: 2 } }}>
            <Section title="Who to call" id="crm-queue-who">
              <Box sx={{ display: 'grid', gap: 1.5 }}>
                {list.search && (
                  <Box>
                    <Chip label={`Search: "${list.search}"`} onDelete={() => changeList({ search: '' })} sx={{ maxWidth: '100%' }} />
                  </Box>
                )}
                <FilterBar
                  state={list}
                  options={options}
                  consent={undefined}
                  matchCount={matched == null ? 'Counting…' : `${people(matched)} match`}
                  onChange={changeList}
                  onClearAll={() => onChange({ ...settings, list: { ...EMPTY_STATE } })}
                  searching={Boolean(list.search)}
                />
                <Typography variant="caption" color="text.secondary">
                  The contact list's filters. On{' '}
                  <Button component={RouterLink} to={contactsLink} size="small" sx={{ p: 0, minWidth: 0, verticalAlign: 'baseline', fontSize: 'inherit' }}>
                    Contacts
                  </Button>
                  , "Call these people" opens the queue with that list's search and filters.
                </Typography>
                {optionsQuery.error && (
                  <Alert severity="warning" variant="outlined" sx={{ py: 0 }}>
                    The tag, list and country choices could not be loaded. The other filters still work.
                  </Alert>
                )}
              </Box>
            </Section>
          </Item>

          <Item sx={{ p: { xs: 1.5, sm: 2 } }}>
            <Section title="In what order" id="crm-queue-order">
              <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: 'minmax(0, 1fr) 220px' }, alignItems: 'start' }}>
                <FormControl>
                  <FormLabel id="crm-queue-mode" sx={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>
                    Who comes first
                  </FormLabel>
                  <RadioGroup
                    aria-labelledby="crm-queue-mode"
                    value={settings.mode}
                    onChange={(event) => onChange({ ...settings, mode: event.target.value as QueueMode })}
                  >
                    {QUEUE_MODES.map((mode) => (
                      <FormControlLabel
                        key={mode.value}
                        value={mode.value}
                        control={<Radio size="small" />}
                        sx={{ alignItems: 'flex-start', mb: 0.75, '& .MuiRadio-root': { pt: 0.25 } }}
                        label={
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {mode.label}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {mode.hint}
                            </Typography>
                          </Box>
                        }
                      />
                    ))}
                  </RadioGroup>
                </FormControl>
                <TextField
                  select
                  size="small"
                  label="Leave out anyone called in the last"
                  value={settings.rest}
                  onChange={(event) => onChange({ ...settings, rest: Number(event.target.value) })}
                  helperText="Unless a follow-up is due."
                >
                  {REST_CHOICES.map((choice) => (
                    <MenuItem key={choice.hours} value={choice.hours}>
                      {choice.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
            </Section>
          </Item>
        </Box>

        <Item sx={{ p: { xs: 1.5, sm: 2 }, position: { md: 'sticky' }, top: { md: 0 } }}>
          <Box component="section" aria-labelledby="crm-queue-count">
            {summaryLoading && !summary && (
              <Box role="status" aria-label="Counting the queue…">
                <Skeleton variant="text" width="50%" height={48} />
                <Skeleton variant="text" />
                <Skeleton variant="text" />
                <Skeleton variant="text" width="70%" />
              </Box>
            )}
            {summaryError && !summary && (
              <Alert severity="error">{describeListError(summaryError).replace('contacts', 'the queue')}</Alert>
            )}
            {summary && (
              <>
                <Typography id="crm-queue-count" variant="h4" component="p" aria-live="polite" sx={{ fontVariantNumeric: 'tabular-nums', lineHeight: 1.1 }}>
                  {n(summary.inQueue)}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  {summary.inQueue === 1 ? 'person to call' : 'people to call'}
                  {summaryLoading ? ', recounting…' : ''}
                </Typography>
                {summaryLoading && <LinearProgress sx={{ height: 2, mb: 1 }} aria-label="Recounting" />}
                <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }} aria-label="In the queue">
                  {rows.map((row, index) => (
                    <Row key={row.key} label={row.label} count={row.count} of={summary.inQueue} tone={['#f59e0b', '#3b82f6', '#6366f1'][index]} />
                  ))}
                </Box>
                {waiting.length > 0 && (
                  <>
                    <Typography variant="overline" color="text.secondary" component="h3" sx={{ display: 'block', mt: 1.5, lineHeight: 2 }}>
                      Not in the queue
                    </Typography>
                    <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }} aria-label="Not in the queue">
                      {waiting.map((row) => (
                        <Box
                          component="li"
                          key={row.key}
                          sx={{ display: 'flex', justifyContent: 'space-between', gap: 1.5, py: 0.25 }}
                        >
                          <Typography variant="body2" color="text.secondary">
                            {row.label}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                            {n(row.count)}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </>
                )}
                {startError && (
                  <Alert severity="error" sx={{ mt: 1.5 }}>
                    {startError}
                  </Alert>
                )}
                <Button
                  fullWidth
                  size="large"
                  variant="contained"
                  startIcon={<PhoneForwardedOutlinedIcon />}
                  onClick={onStart}
                  disabled={starting || summary.inQueue === 0}
                  sx={{ mt: 2 }}
                >
                  {starting ? 'Finding the first person…' : 'Start calling'}
                </Button>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  component="p"
                  sx={(theme) => ({ mt: 1, p: 1, borderRadius: 1, bgcolor: alpha(theme.palette.primary.main, 0.04) })}
                >
                  Each person is held for you while you call, so two callers on one list never ring the same person. Do
                  not call, do not contact and people with no number are never served, whatever the filters say.
                </Typography>
              </>
            )}
          </Box>
        </Item>
      </Box>
    </Box>
  );
};
